import { useCallback, useRef, useState } from "react";
import { createPersonaInquiry } from "services/personaInquiryService";

type PersonaCompleteResult = {
  inquiryId: string;
  status: string;
  fields: Record<string, unknown>;
};

type PersonaSdkClient = {
  open: () => void;
  cancel: (force: boolean) => void;
};

type OpenPersonaFlowParams = {
  prospectId: string;
  proposalId: string;
  contactId: string;
  hybridToken?: string;
  firstName?: string;
  lastName?: string;
  birthdate?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  onInquiryFinished?: () => void;
  onError?: (message: string) => void;
};

const usePersonaEmbeddedFlow = () => {
  const clientRef = useRef<PersonaSdkClient | null>(null);
  const [isOpening, setIsOpening] = useState(false);

  const destroyClient = useCallback(() => {
    if (clientRef.current) {
      try {
        clientRef.current.cancel(false);
      } catch {
        // Persona client may already be torn down
      }
      clientRef.current = null;
    }
    setIsOpening(false);
  }, []);

  const openPersonaFlow = useCallback(
    async ({
      prospectId,
      proposalId,
      contactId,
      hybridToken,
      firstName,
      lastName,
      birthdate,
      street,
      city,
      state,
      postalCode,
      onInquiryFinished,
      onError,
    }: OpenPersonaFlowParams) => {
      if (!prospectId || !proposalId || !contactId) {
        onError?.("Unable to start identity verification. Please try again.");
        return;
      }

      setIsOpening(true);

      try {
        const { inquiryId, sessionToken } = await createPersonaInquiry({
          prospectId,
          proposalId,
          contactId,
          hybridToken,
          firstName,
          lastName,
          birthdate,
          street,
          city,
          state,
          postalCode,
          inquiryType: "verify",
        });

        const persona = await import("persona");
        let client: PersonaSdkClient;
        client = new persona.Client({
          inquiryId,
          ...(sessionToken ? { sessionToken } : {}),
          onReady: () => client.open(),
          onComplete: (result: PersonaCompleteResult) => {
            console.log("Persona identity verification complete", result);
            onInquiryFinished?.();
            destroyClient();
          },
          onCancel: () => {
            console.log("Persona identity verification cancelled");
            destroyClient();
          },
          onError: (error: unknown) => {
            console.error("Persona identity verification error", error);
            destroyClient();
          },
        });
        clientRef.current = client;
      } catch (error) {
        console.error("Failed to start Persona identity verification", error);
        setIsOpening(false);
        onError?.("Unable to open identity verification. Please try again.");
      }
    },
    [destroyClient]
  );

  return { openPersonaFlow, isOpening, destroyClient };
};

export {
  usePersonaEmbeddedFlow,
  type PersonaCompleteResult,
  type OpenPersonaFlowParams,
};
